import { Box, IconButton, Tooltip } from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridRowSelectionModel,
} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ManualMasterPartListItem } from "@/api/manualMasterPartService";
import { formattedDateTime } from "@/utils/date";

interface ManualMasterPartTableProps {
  data: ManualMasterPartListItem[];
  isLoading: boolean;
  canEdit: boolean;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onDelete: (row: ManualMasterPartListItem) => void;
}

const ManualMasterPartTable = ({
  data,
  isLoading,
  canEdit,
  rowSelectionModel,
  onRowSelectionModelChange,
  onDelete,
}: ManualMasterPartTableProps) => {
  const columns: GridColDef<ManualMasterPartListItem>[] = [
    {
      field: "action",
      headerName: "Action",
      width: 80,
      headerAlign: "center",
      align: "center",
      headerClassName: "custom-header",
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Tooltip title="ลบ" placement="top">
            <IconButton onClick={() => onDelete(params.row)} disabled={!canEdit}>
              <DeleteIcon color={canEdit ? "error" : "disabled"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "part_number",
      headerName: "Part Number",
      minWidth: 150,
      flex: 0.2,
      headerClassName: "custom-header",
    },
    {
      field: "part_name",
      headerName: "Part Name",
      minWidth: 200,
      flex: 0.3,
      headerClassName: "custom-header",
    },
    {
      field: "ac",
      headerName: "AC",
      width: 100,
      headerClassName: "custom-header",
    },
    {
      field: "rt",
      headerName: "Routing",
      width: 100,
      headerClassName: "custom-header",
    },
    {
      field: "supplier_code",
      headerName: "Supplier Code",
      width: 140,
      headerClassName: "custom-header",
    },
    {
      field: "manual_by",
      headerName: "เพิ่มโดย",
      width: 130,
      headerClassName: "custom-header",
    },
    {
      field: "manual_date",
      headerName: "วันที่เพิ่ม",
      width: 170,
      headerClassName: "custom-header",
      valueFormatter: (value?: string) =>
        value ? formattedDateTime(value) : "-",
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        checkboxSelection={canEdit}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          // header checkbox cell ไม่ได้รับพื้นหลัง teal จาก theme (.MuiDataGrid-columnHeader)
          // อัตโนมัติเหมือน header อื่น และ checkbox สี default มองไม่เห็นบนพื้นเขียว — บังคับสีเอง
          "& .MuiDataGrid-columnHeaderCheckbox": {
            backgroundColor: "#00A8A9 !important",
          },
          "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
            color: "#FFFFFF !important",
          },
        }}
      />
    </Box>
  );
};

export default ManualMasterPartTable;
